import http from '@/services/http';

/**
 * Vuex module for caja apertura/cierre — apertura EXCLUSIVA por usuario (#363-exclusividad).
 *
 * Dominio DISTINTO de `cajasCatalogo` (catálogo de cajas físicas, `cat_cajas`): acá se habla de
 * `tbl_caja_apertura_cierre` (la fila de apertura de UN cajero), cajasCatalogo habla del aparato
 * físico. Mismo criterio de separación que ya documenta `cajas_catalogo_routes.js` en el backend.
 *
 * Shape mirrors `store/modules/customers.js` (bisstrack-front-conventions gold reference).
 *
 * `abrirCaja` y `getCajaTurno` devuelven la respuesta CRUDA — ni `ya_estaba_abierta` ni el 409
 * (`codigo: CAJA_OCUPADA | USUARIO_CON_OTRA_CAJA`) se interpretan acá: esa decisión es del
 * componente/util que llama (AperturaCajaCard.vue, sincronizarCajaAbierta.js), igual que
 * `asignarTerminal` en `cajasCatalogo.js` no interpreta `requiere_confirmacion`.
 */

const state = () => ({
  loading: false,
  error: null,
});

const getters = {
  isLoading: (state) => state.loading,
  hasError: (state) => state.error,
};

const mutations = {
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_ERROR(state, value) {
    state.error = value;
  },
};

const actions = {
  // GET /cajas/usuario/:id_usuario — la apertura abierta del usuario actual (o vacía si no
  // tiene ninguna). Fuente de verdad para "tengo caja abierta" (#363-exclusividad): reemplaza
  // la lectura ciega de localStorage["id_caja"] que hacían Pos.vue/NotaCredito.vue.
  async getCajaTurno({ commit }, idUsuario) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.get(`cajas/usuario/${idUsuario}`);
      commit('SET_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al consultar la caja del usuario');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // POST /cajas/apertura. 200 con `ya_estaba_abierta` true/false son ambos éxito (el body ES
  // la apertura). 409 (`codigo: CAJA_OCUPADA | USUARIO_CON_OTRA_CAJA`) propaga tal cual — el
  // componente muestra `error.response.data.message` como vino del backend, sin reinterpretarlo.
  async abrirCaja({ commit }, { id_usuario, monto_inicial, id_caja }) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.post('cajas/apertura', { id_usuario, monto_inicial, id_caja });
      commit('SET_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || error.message || 'Error al abrir la caja');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
